import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Menu, X, Heart, Eye, EyeOff, LogOut, Shield, User, Building, ChevronRight, ArrowLeft, Plus, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const { t } = useTranslation();
  const { user, login, register, verifyCode, resendCode, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [registerStep, setRegisterStep] = useState(1);
  const [selectedMembershipType, setSelectedMembershipType] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dynamicPages, setDynamicPages] = useState([]);
  const [hasListings, setHasListings] = useState(false);

  // Check if user has listings
  useEffect(() => {
    if (user) {
      axios.get(`${API}/my-listings`, { headers: { Authorization: `Bearer ${localStorage.getItem('freelancehub_token')}` } })
        .then(res => setHasListings(res.data.length > 0))
        .catch(() => setHasListings(false));
    }
  }, [user]);

  // Track scroll for header style change
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const fetchSettings = async () => {
    try {
      const [settingsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/settings/public`),
        axios.get(`${API}/pages/public`)
      ]);
      setSettings(settingsRes.data);
      setDynamicPages(pagesRes.data.filter(p => p.show_in_menu));
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const branding = settings?.branding || {};
  const content = settings?.content || {};

  // Apply favicon dynamically
  useEffect(() => {
    if (branding.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = branding.favicon_url;
    }
  }, [branding.favicon_url]);
  const membershipTypes = (settings?.membership_types || []).filter(t => t.active).sort((a, b) => a.order - b.order);

  const getMembershipIcon = (iconName) => {
    switch (iconName) {
      case 'building': return <Building className="w-6 h-6" />;
      case 'eye': return <Eye className="w-6 h-6" />;
      default: return <User className="w-6 h-6" />;
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getLinkClass = (path) => {
    const baseClass = "transition-colors font-medium";
    if (isActive(path)) {
      return `${baseClass} text-white border-b-2 border-emerald-500 pb-1`;
    }
    return `${baseClass} text-zinc-400 hover:text-white`;
  };

  const getMobileLinkClass = (path) => {
    if (isActive(path)) {
      return "text-emerald-400 font-semibold border-l-2 border-emerald-500 pl-3";
    }
    return "text-zinc-300 font-medium pl-3 hover:text-white";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (authMode === 'login') {
        await login(formData.email, formData.password);
        setShowAuthModal(false);
        setFormData({ name: '', email: '', password: '' });
        setShowPassword(false);
        setRegisterStep(1);
        setSelectedMembershipType(null);
      } else {
        const result = await register(formData.name, formData.email, formData.password, selectedMembershipType || 'normal');
        if (result.requires_verification) {
          setVerificationEmail(formData.email);
          setShowVerification(true);
          setVerificationCode('');
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Bir hata oluştu. Tekrar deneyin.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await verifyCode(verificationEmail, verificationCode);
      setShowAuthModal(false);
      setShowVerification(false);
      setFormData({ name: '', email: '', password: '' });
      setVerificationCode('');
      setRegisterStep(1);
      setSelectedMembershipType(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Geçersiz kod';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendCode(verificationEmail);
      setError('');
    } catch (err) {
      setError('Kod gönderilemedi');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'glass py-3 shadow-lg shadow-black/30'
          : 'bg-black py-4 border-b border-zinc-800'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              {branding.logo_url ? (
                <>
                  {/* Mobile logo */}
                  <img
                    src={branding.mobile_logo_url || branding.logo_url}
                    alt={content.site_title || 'Logo'}
                    className="md:hidden"
                    style={{
                      width: branding.mobile_logo_url && branding.mobile_logo_width ? branding.mobile_logo_width : (branding.mobile_logo_url ? 'auto' : Math.min(branding.logo_width || 150, 140)),
                      height: branding.mobile_logo_url && branding.mobile_logo_height ? branding.mobile_logo_height : 'auto',
                      maxWidth: '140px',
                      objectFit: 'contain'
                    }}
                  />
                  {/* Desktop logo */}
                  <img
                    src={branding.logo_url}
                    alt={content.site_title || 'Logo'}
                    className="hidden md:block"
                    style={{
                      width: branding.logo_width || 150,
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                </>
              ) : null}
              {(!branding.logo_url) && content.site_title && (
                <span className="text-xl sm:text-2xl md:text-3xl font-bold">
                  <span className="text-white">{content.site_title.split(' ')[0]}</span>
                  {content.site_title.split(' ')[1] && <span className="text-emerald-500">{content.site_title.split(' ')[1]}</span>}
                </span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {dynamicPages.sort((a, b) => a.menu_order - b.menu_order).map(page => (
                <Link
                  key={page.id}
                  to={`/page/${page.slug}`}
                  className="text-zinc-400 hover:text-white transition-colors font-medium"
                >
                  {page.title}
                </Link>
              ))}
              {user ? (
                <div className="flex items-center gap-3">
                  <Link to="/favorites" className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-medium">
                    <Heart className="w-4 h-4" />
                    {t('header.favorites')}
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 transition-colors font-medium">
                      <Shield className="w-4 h-4" />
                      {t('header.admin')}
                    </Link>
                  )}
                  <Link to="/profile" className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-medium">
                    <User className="w-4 h-4" />
                    {t('header.profile')}
                  </Link>
                  <button onClick={handleLogout} className="flex items-center gap-1.5 text-zinc-400 hover:text-red-400 transition-colors font-medium">
                    <LogOut className="w-4 h-4" />
                    {t('common.logout')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all hover:shadow-lg hover:shadow-emerald-500/20"
                >
                  {t('common.login')} / {t('common.signup')}
                </button>
              )}
            </nav>

            {/* Mobile Buttons */}
            <div className="lg:hidden relative">
              {user ? (
                <>
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="text-zinc-400 hover:text-white p-2"
                  >
                    <User className="w-5 h-5" />
                  </button>
                  {mobileMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMobileMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl z-50 py-2 overflow-hidden">
                        <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
                          <User className="w-4 h-4" /> Profil
                        </Link>
                        {hasListings ? (
                          <Link to="/my-listings" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
                            <FileText className="w-4 h-4" /> İlanlarım
                          </Link>
                        ) : (
                          <Link to="/create-listing" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
                            <Plus className="w-4 h-4" /> İlan Ekle
                          </Link>
                        )}
                        <Link to="/favorites" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
                          <Heart className="w-4 h-4" /> Favoriler
                        </Link>
                        {user.role === 'admin' && (
                          <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-purple-400 hover:bg-zinc-800 transition-colors">
                            <Shield className="w-4 h-4" /> Admin Panel
                          </Link>
                        )}
                        <div className="border-t border-zinc-700 my-1" />
                        <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-zinc-800 w-full transition-colors">
                          <LogOut className="w-4 h-4" /> Çıkış
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <button
                  onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
                >
                  Giriş/Kayıt
                </button>
              )}
            </div>
          </div>
        </div>
      </header>


      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={(open) => {
        setShowAuthModal(open);
        if (!open) { setRegisterStep(1); setSelectedMembershipType(null); setError(''); setShowVerification(false); setVerificationCode(''); }
      }}>
        <DialogContent className={`bg-zinc-900 border-zinc-700 text-white ${authMode === 'register' && registerStep === 1 && !showVerification ? 'sm:max-w-lg' : 'sm:max-w-md'}`}>

          {/* Verification Code Screen */}
          {showVerification ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-center">E-posta Doğrulama</DialogTitle>
              </DialogHeader>
              <div className="text-center mt-2">
                <p className="text-zinc-400 text-sm mb-1">Doğrulama kodu gönderildi:</p>
                <p className="text-emerald-400 font-medium">{verificationEmail}</p>
              </div>
              <form onSubmit={handleVerify} className="space-y-4 mt-4">
                {error && <p className="text-red-400 text-sm text-center bg-red-500/10 rounded-lg py-2">{error}</p>}
                <div>
                  <Label className="text-zinc-300 text-sm">6 Haneli Doğrulama Kodu</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="bg-zinc-800 border-zinc-700 text-white text-center text-2xl tracking-[0.5em] font-bold mt-1"
                    placeholder="000000"
                    autoFocus
                    required
                  />
                </div>
                <Button type="submit" disabled={submitting || verificationCode.length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2.5">
                  {submitting ? 'Doğrulanıyor...' : 'Doğrula'}
                </Button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="w-full text-zinc-400 hover:text-white text-sm transition-colors"
                >
                  {resending ? 'Gönderiliyor...' : 'Kodu tekrar gönder'}
                </button>
              </form>
            </>
          ) : (
          <>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">
              {authMode === 'login'
                ? t('auth.login')
                : registerStep === 1
                  ? t('auth.selectMembershipType', 'Üyelik Tipini Seçin')
                  : t('auth.signup')}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Membership Type Selection (Register only) */}
          {authMode === 'register' && registerStep === 1 ? (
            <div className="space-y-3 mt-4">
              {membershipTypes.map((type) => (
                <div
                  key={type.id}
                  onClick={() => setSelectedMembershipType(type.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedMembershipType === type.id
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedMembershipType === type.id ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {getMembershipIcon(type.icon)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold text-lg">{type.title}</h3>
                        <div className="flex items-center gap-2">
                          {/* Price Badge */}
                          {type.is_free !== false ? (
                            <span className="text-xs font-bold px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400">
                              {t('membership.free', 'Ücretsiz')}
                            </span>
                          ) : (
                            <span className="text-sm font-bold text-amber-400">
                              {type.price}{type.currency || '₺'}/{t('membership.month', 'ay')}
                            </span>
                          )}
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            selectedMembershipType === type.id ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-600'
                          }`}>
                            {selectedMembershipType === type.id && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Duration info */}
                      {type.duration_days && (
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {type.duration_days} {t('membership.days', 'gün')}
                          {type.is_free !== false && ` ${t('membership.freeTrial', 'ücretsiz deneme')}`}
                        </p>
                      )}
                      <ul className="mt-2 space-y-1">
                        {(type.description_bullets || []).map((bullet, i) => (
                          <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                            <span className="text-emerald-500 mt-0.5">•</span>
                            {bullet}
                          </li>
                        ))}
                      </ul>
                      {/* Note */}
                      {type.note && (
                        <p className={`mt-2 text-xs italic px-2 py-1 rounded ${
                          type.is_free !== false
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-amber-500/10 text-amber-400'
                        }`}>
                          {type.note}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <Button
                onClick={() => { if (selectedMembershipType) setRegisterStep(2); }}
                disabled={!selectedMembershipType}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 mt-2 py-5"
              >
                {t('auth.continue', 'Devam Et')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  className="text-zinc-400 hover:text-emerald-400 text-sm transition-colors"
                  onClick={() => { setAuthMode('login'); setError(''); }}
                >
                  {t('auth.hasAccount')}
                </button>
              </div>
            </div>
          ) : (
            /* Step 2: Login / Register Form */
            <>
              {authMode === 'register' && registerStep === 2 && (
                <button
                  type="button"
                  onClick={() => setRegisterStep(1)}
                  className="flex items-center gap-1 text-zinc-400 hover:text-white text-sm transition-colors mt-2"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t('auth.backToSelection', 'Geri Dön')}
                </button>
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                {authMode === 'register' && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-300">
                      {t('auth.name')} <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      autoComplete="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-zinc-800 border-zinc-600 text-white focus:border-emerald-500 focus:ring-emerald-500/20"
                      placeholder={t('auth.enterName')}
                      required
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">
                    {t('auth.email')} <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-600 text-white focus:border-emerald-500 focus:ring-emerald-500/20"
                    placeholder="example@email.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-zinc-300">
                    {t('auth.password')} <span className="text-red-400">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-zinc-800 border-zinc-600 text-white pr-10 focus:border-emerald-500 focus:ring-emerald-500/20"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2 rounded-lg">{error}</p>}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                >
                  {submitting ? t('common.loading') : (authMode === 'login' ? t('auth.login') : t('auth.signup'))}
                </Button>
              </form>
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="text-zinc-400 hover:text-emerald-400 text-sm transition-colors"
                  onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setRegisterStep(1); setError(''); setShowPassword(false); }}
                >
                  {authMode === 'login' ? (
                    <>{t('auth.noAccount')} <span className="text-emerald-400 font-semibold">{t('common.signup')}</span></>
                  ) : (
                    <>{t('auth.hasAccount')} <span className="text-emerald-400 font-semibold">{t('common.login')}</span></>
                  )}
                </button>
              </div>
            </>
          )}
          </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
